/* eslint-disable react/forbid-prop-types */
// prop-type is a library for typechecking of props
import { useMemo } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MuiPhoneNumber from "material-ui-phone-number";

// NewUser page components
import FormField from "components/FormField";
import { Grid, Autocomplete } from "@mui/material";
import MDTypography from "components/MDTypography";
import { ErrorMessage } from "formik";
import MDInput from "components/MDInput";

const statuses = [
  {
    label: "Active",
    id: "A",
  },
  {
    label: "On Leave",
    id: "L",
  },
  {
    label: "Pending",
    id: "P",
  },
  {
    label: "Terminated",
    id: "T",
  },
];
function ContractorForm({ formData, locations, jobs, companies, typeCodes }) {
  const { formField, values, errors, touched, setFieldValue } = formData;
  const {
    firstName,
    lastName,
    email,
    workPhoneNumber,
    code,
    ssn,
    subCompany,
    location,
    job,
    rate,
    status,
    typeCode,
  } = formField;
  const {
    firstName: firstNameV,
    lastName: lastNameV,
    email: emailV,
    workPhoneNumber: workPhoneNumberV,
    code: codeV,
    ssn: ssnV,
    subCompany: subCompanyV,
    location: locationV,
    job: jobV,
    rate: rateV,
    status: statusV,
    typeCode: typeCodeV,
  } = values;

  const statusValueOptionFormat = useMemo(() => {
    const s = statuses.find((sItr) => sItr.id === statusV);
    if (s) {
      return s;
    }
    return null;
  }, [statuses, statusV]);

  const typeCodeValueOptionFormat = useMemo(() => {
    const s = typeCodes.find((sItr) => sItr.id === typeCodeV);
    if (s) {
      return s;
    }
    return null;
  }, [typeCodes, typeCodeV]);

  const locationValueOptionFormat = useMemo(() => {
    const lc = locations.find((locationItr) => locationItr.id === locationV);
    if (lc) {
      return {
        id: lc.id,
        label: lc.name,
      };
    }
    return null;
  }, [locations, locationV]);

  const jobValueOptionFormat = useMemo(() => {
    const j = jobs.find((jobItr) => jobItr.id === jobV);
    if (j) {
      return {
        id: j.id,
        label: j.name,
      };
    }
    return null;
  }, [jobs, jobV]);

  const compValueOptionFormat = useMemo(() => {
    const c = companies.find((compItr) => compItr.id === subCompanyV);
    if (c) {
      return {
        id: c.id,
        label: c.name,
      };
    }
    return null;
  }, [companies, subCompanyV]);

  const locationOptions = useMemo(
    () =>
      locations.map((l) => ({
        label: l.name,
        id: l.id,
      })),
    [locations]
  );

  const jobOptions = useMemo(
    () =>
      jobs.map((l) => ({
        label: l.name,
        id: l.id,
      })),
    [jobs]
  );

  const companyOptions = useMemo(
    () =>
      companies.map((l) => ({
        label: l.name,
        id: l.id,
      })),
    [companies]
  );

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormField
            type={firstName.type}
            label={firstName.label}
            name={firstName.name}
            value={firstNameV}
            placeholder={firstName.placeholder}
            error={errors.firstName && touched.firstName}
            success={firstNameV && firstNameV.length > 0 && !errors.firstName}
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            type={lastName.type}
            label={lastName.label}
            name={lastName.name}
            value={lastNameV}
            placeholder={lastName.placeholder}
            error={errors.lastName && touched.lastName}
            success={lastNameV && lastNameV.length > 0 && !errors.lastName}
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            type={email.type}
            label={email.label}
            name={email.name}
            value={emailV}
            placeholder={email.placeholder}
            error={errors.email && touched.email}
            success={emailV && emailV.length > 0 && !errors.email}
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            type={code.type}
            label={code.label}
            name={code.name}
            disabled
            value={codeV}
            placeholder={code.placeholder}
            error={errors.code && touched.code}
            success={codeV && codeV.length > 0 && !errors.code}
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            type={ssn.type}
            label={ssn.label}
            name={ssn.name}
            value={ssnV}
            placeholder={ssn.placeholder}
            error={errors.ssn && touched.ssn}
            success={ssnV && ssnV.length > 0 && !errors.ssn}
          />
        </Grid>
        <Grid item xs={12}>
          <MuiPhoneNumber
            label={workPhoneNumber.label}
            name={workPhoneNumber.name}
            variant="outlined"
            fullWidth
            defaultCountry="us"
            value={workPhoneNumberV}
            error={!!errors.workPhoneNumber}
            success={workPhoneNumberV && workPhoneNumberV.length > 0 && !errors.workPhoneNumber}
            onChange={(phone) => {
              setFieldValue(workPhoneNumber.name, phone);
            }}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage name={workPhoneNumber.name} />
            </MDTypography>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            value={compValueOptionFormat}
            options={companyOptions}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(subCompany.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option?.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={subCompany.label}
                name={subCompany.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.subCompany && touched.subCompany}
                success={subCompanyV && subCompanyV.length > 0 && !errors.subCompany}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={subCompany.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            value={locationValueOptionFormat}
            options={locationOptions}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(location.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option?.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={location.label}
                name={location.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.location && touched.location}
                success={locationV && locationV.length > 0 && !errors.location}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={location.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            value={jobValueOptionFormat}
            options={jobOptions}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(job.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option?.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={job.label}
                name={job.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.job && touched.job}
                success={jobV && jobV.length > 0 && !errors.job}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={job.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <FormField
            type={rate.type}
            label={rate.label}
            name={rate.name}
            value={rateV}
            placeholder={rate.placeholder}
            error={errors.rate && touched.rate}
            success={rateV && rateV.length > 0 && !errors.rate}
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            multiple={false}
            value={typeCodeValueOptionFormat}
            options={typeCodes}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(typeCode.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option?.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={typeCode.label}
                name={typeCode.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.typeCode && touched.typeCode}
                success={typeCodeV && typeCodeV.length > 0 && !errors.typeCode}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={typeCode.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            multiple={false}
            value={statusValueOptionFormat}
            options={statuses}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(status.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option?.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={status.label}
                name={status.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.status && touched.status}
                success={statusV && statusV.length > 0 && !errors.status}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={status.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

// typechecking props for NumberForm
ContractorForm.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  locations: PropTypes.arrayOf(PropTypes.object).isRequired,
  jobs: PropTypes.arrayOf(PropTypes.object).isRequired,
  companies: PropTypes.arrayOf(PropTypes.object).isRequired,
  typeCodes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default ContractorForm;
